import React, { useState, useEffect } from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import ReactDOM from "react-dom";
import { COPY_STRINGS } from "Constants/translatedCopy";
import { sleep, isDerangement, shuffle } from "../shared/util";
import { Container, Box } from "../shared";
import useConversation from "../shared/useConversation";
import { listen } from "Services/Listen";
import { getUserData } from "Services/API";
import ListeningStatus from "Components/Global/ListeningStatus";
import useValidation from "../shared/useValidation";
import useTimer from "../shared/useTimer";
import "./WordScramble.scss";
import icon from "./wordscramble-icon.png";
import useIsMounted from "../shared/hooks/useIsMounted";
import useCancellablePromise from "../shared/hooks/useCancellablePromise";

let ttsStrings;

const colors = {
  yellow: "#FFDC00",
  green: "#2ECC40",
  blue: "#7FDBFF",
};

const DEMO_WORD = "wag";
const DEMO_GRAPHEMES = ["w", "a", "g"];

export default function WordScrambleValidation(props) {
  ttsStrings = COPY_STRINGS.intervention_strings.word_scramble;

  // must be between 3 and 6 letters
  const n = props.word.length;
  const isCorrectLength = n >= 3 && n <= 6;

  const isValid =
    useValidation(props.version, props.wordMetadata, {
      1: ["GRAPHEMIC_BREAKDOWN", "PHON"],
    }) && isCorrectLength;

  const isMountedRef = useIsMounted();

  useEffect(() => {
    if (!isValid) close();
  }, []);

  if (!isValid) return null;

  const Version = {
    1: WordScramble,
  }[props.version];

  return <Version {...props} close={close} isMountedRef={isMountedRef} />;

  function close() {
    if (!isMountedRef.current) return;

    props.handleMessage({
      continueWithoutSpeaking: true,
      endOfPhrase: props.endOfPhrase,
    });
  }
}

function WordScramble(props) {
  const { word, wordMetadata, speech, ftue } = props;
  const { GRAPHEMIC_BREAKDOWN, PHON } = wordMetadata;
  const graphemes = GRAPHEMIC_BREAKDOWN.split("/").map((g, i) => `${g}_${i}`);

  const [stage, setStage] = useState(ftue ? 0 : 1);

  // working scramble of the graphemes
  const [scramble, setScramble] = useState(getInitScramble());

  function getInitScramble() {
    let out = shuffle(graphemes);
    while (!isDerangement(graphemes, out)) out = shuffle(graphemes);
    return out;
  }

  // background color of the boxes
  const [highlight, setHighlight] = useState(null);

  // are the letters scrambled in the demo?
  const [isScrambled, setIsScrambled] = useState(false);

  const [dragDisabled, setDragDisabled] = useState(true);
  const [revealAnswer, setRevealAnswer] = useState(false);

  // vary timeout length depending on word length
  const { startTimer, clearTimer } = useTimer({
    timeout: 15000 + 1000 * word.length,
    onEnd: onTimeout,
  });

  const { speak, speakPhonemes, listenWord, speakSequence } = useConversation(speech, listen);
  const { cancellablePromise } = useCancellablePromise();

  // handle stages
  useEffect(() => {
    onStageChange();
  }, [stage]);

  async function onStageChange() {
    if (!props.isMountedRef.current) return;

    try {
      switch (stage) {
        case 0:
          if(getUserData().bilingualMode) {
            await cancellablePromise(speakSequence([
              ttsStrings.before_we_look_at,
              {textToSpeak: word, skipTranslation: true},
              ttsStrings.lets_talk_about,
              {textToSpeak: DEMO_WORD, skipTranslation: true},
            ]));
          } else {
            await cancellablePromise(speak(`${ttsStrings.before_we_look_at} "${word}", ${ttsStrings.lets_talk_about} "${DEMO_WORD}".`));
          }
          await cancellablePromise(sleep());
          setIsScrambled(true);
          await cancellablePromise(speak(ttsStrings.scrambled));
          await cancellablePromise(sleep(500));
          setHighlight(colors.yellow);
          setIsScrambled(false);
          await cancellablePromise(sleep());
          setHighlight(colors.green);
          await cancellablePromise(sleep(500));
          await cancellablePromise(speak(ttsStrings.fix_scrambled));
          await cancellablePromise(sleep());
          setStage(1);
          break;
        case 1: {
          setIsScrambled(false);
          setHighlight(colors.green);
          if (ftue) await cancellablePromise(speak(ttsStrings.your_turn));
          await cancellablePromise(speak(ttsStrings.ask_word_1));
          const { status } = await cancellablePromise(listenWord({
            targetWord: word,
            patterOverride: ["match", "struggle", "silence"],
          }));
          if (status === "skip") return props.close();
          if (["struggle", "silence"].includes(status)) {
            await cancellablePromise(speak(ttsStrings.sound_out));
            await cancellablePromise(sleep());
            await cancellablePromise(speakPhonemes({ phon: PHON }));
            await cancellablePromise(speak(ttsStrings.ask_word_2));
            const { status } = await cancellablePromise(listenWord({
              targetWord: word,
              patterOverride: ["match", "struggle", "silence"],
            }));
            if (status === "skip") return props.close();
          }
          setIsScrambled(true);
          setHighlight(colors.yellow);
          await cancellablePromise(speak(ttsStrings.ask_unscramble));
          setDragDisabled(false);
          startTimer();
          break;
        }
      }
    } catch (error) {
      if (error.isCancelled) return;
      throw(error);
    }
  }

  return (
    <Container icon={icon}>
      {renderContent()}
      <div style={{ marginTop: 10 }}>
        <ListeningStatus />
      </div>
    </Container>
  );

  function renderContent() {
    if (stage === 0) {
      let offsets = [0, 1, 2];
      if (isScrambled) offsets = [1, 0, 2];
      return (
        <div className="ws-boxes ws-demo">
          {DEMO_GRAPHEMES.map((grapheme, i) => {
            const boxStyle = { left: offsets[i] * 160 };
            if (highlight) boxStyle.background = highlight;
            return (
              <Box key={`0/${i}`} className="ws-box ws-demo" style={boxStyle}>
                <div className="ws-letter">{grapheme}</div>
              </Box>
            );
          })}
        </div>
      );
    }

    if (revealAnswer) {
      return (
        <div className="ws-boxes">
          {graphemes.map((grapheme) => {
            const boxStyle = {};
            if (highlight) boxStyle.background = highlight;
            return (
              <Box key={grapheme} className="ws-box" style={boxStyle}>
                <div className="ws-letter">{grapheme.split("_")[0]}</div>
              </Box>
            );
          })}
        </div>
      );
    }

    let order = graphemes;
    if (isScrambled) order = scramble;
    return (
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="droppable" direction="horizontal">
          {(provided) => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              className="ws-boxes"
            >
              {order.map((item, i) => (
                <Draggable
                  key={item}
                  draggableId={item}
                  index={i}
                  isDragDisabled={dragDisabled}
                >
                  {(provided, snapshot) => {
                    return (
                      <DraggablePortalHandler snapshot={snapshot}>
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                        >
                          <Box
                            className="ws-box"
                            style={{ background: highlight }}
                          >
                            <div className="ws-letter">
                              {item.split("_")[0]}
                            </div>
                          </Box>
                        </div>
                      </DraggablePortalHandler>
                    );
                  }}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    );
  }

  async function onDragEnd(result) {
    if (!props.isMountedRef.current) return;
    
    try {
      const { destination, source, draggableId } = result;
      if (!destination || !source || destination.index === source.index) return;
      const update = Array.from(scramble);
      update.splice(source.index, 1);
      update.splice(destination.index, 0, draggableId);
      setScramble(update);

      // check for match
      const updateWord = update.map((g) => g.split("_")[0]).join("");
      if (updateWord === word) {
        setDragDisabled(true);
        setHighlight(colors.green);
        clearTimer();
        if(getUserData().bilingualMode) {
          await cancellablePromise(speakSequence([
            ttsStrings.you_got,
            {textToSpeak: word, skipTranslation: true},
            ttsStrings.back_in_order,
            ttsStrings.fantastic_job,
          ]));
        } else {
          await cancellablePromise(speak(`${ttsStrings.you_got} "${word}", ${ttsStrings.back_in_order}. ${ttsStrings.fantastic_job}.`));
        }
        props.close();
      }
    } catch (error) {
      if (error.isCancelled) return;
      throw(error);
    }
  }

  async function onTimeout() {
    if (!props.isMountedRef.current) return;
    try {
      setDragDisabled(true);
      setHighlight(colors.blue);
      await cancellablePromise(sleep(3000));
      setRevealAnswer(true);
      setHighlight(colors.green);
      if(getUserData().bilingualMode) {
        await cancellablePromise(speakSequence([
          ttsStrings.this_is_our_word,
          {textToSpeak: word, skipTranslation: true},
          ttsStrings.unscrambled,
          ttsStrings.lets_do_some_reading,
        ]));
      } else {
        await cancellablePromise(speak(`${ttsStrings.this_is_our_word} "${word}", ${ttsStrings.unscrambled}. ${ttsStrings.lets_do_some_reading}.`));
      }
      props.close();
    } catch (error) {
      if (error.isCancelled) return;
      throw(error);
    }
  }
}

// fix drag offset in lexa-studentapp
function DraggablePortalHandler({ children, snapshot }) {
  if (snapshot.isDragging)
    return ReactDOM.createPortal(children, document.body);
  return children;
}
